import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { StaffPermissionKey } from "@/lib/staff-permissions";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  rolesLoading: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  staffId: string | null;
  staffStatus: string | null;
  staffRole: string | null;
  staffPermissions: Record<string, boolean>;
  can: (perm: StaffPermissionKey) => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  rolesLoading: true,
  isAdmin: false,
  isStaff: false,
  staffId: null,
  staffStatus: null,
  staffRole: null,
  staffPermissions: {},
  can: () => false,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [staffStatus, setStaffStatus] = useState<string | null>(null);
  const [staffRole, setStaffRole] = useState<string | null>(null);
  const [staffPermissions, setStaffPermissions] = useState<Record<string, boolean>>({});

  const checkRoles = async (userId: string) => {
    setRolesLoading(true);
    try {
      // Check admin
      const { data: adminData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!adminData);

      // Check if this user is a staff member
      const { data: staffData } = await supabase
        .from("staff")
        .select("id, is_active, access_status, access_role, permissions")
        .eq("user_id", userId)
        .maybeSingle();

      const approved = !!staffData && staffData.is_active && staffData.access_status === "approved";
      setIsStaff(approved);
      setStaffId(approved ? staffData!.id : null);
      setStaffStatus(staffData ? (staffData.access_status as string) : null);
      setStaffRole(staffData ? (staffData.access_role as string) : null);
      setStaffPermissions(
        approved ? ((staffData!.permissions as Record<string, boolean>) || {}) : {}
      );
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    const clearRoles = () => {
      setIsAdmin(false);
      setIsStaff(false);
      setStaffId(null);
      setStaffStatus(null);
      setStaffRole(null);
      setStaffPermissions({});
      setRolesLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // TOKEN_REFRESHED keeps existing roles; only re-check on sign-in events
          if (event === "SIGNED_IN" || event === "INITIAL_SESSION" || event === "USER_UPDATED") {
            setTimeout(() => checkRoles(session.user.id), 0);
          }
        } else if (event === "SIGNED_OUT") {
          clearRoles();
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        checkRoles(session.user.id);
      } else {
        setRolesLoading(false);
      }
      setLoading(false);
    });

    // Keep the token fresh: when the tab regains focus, ask Supabase to
    // refresh the session if it is close to expiring (never logs the user out).
    const keepAlive = () => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          setSession(session);
          setUser(session.user);
        }
      });
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") keepAlive();
    };
    window.addEventListener("focus", keepAlive);
    document.addEventListener("visibilitychange", onVisible);
    const interval = window.setInterval(keepAlive, 10 * 60 * 1000);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("focus", keepAlive);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    setIsStaff(false);
    setStaffId(null);
    setStaffStatus(null);
    setStaffRole(null);
    setStaffPermissions({});
  };

  const can = (perm: StaffPermissionKey) => isAdmin || staffPermissions[perm] === true;

  return (
    <AuthContext.Provider value={{ user, session, loading, rolesLoading, isAdmin, isStaff, staffId, staffStatus, staffRole, staffPermissions, can, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
